import React, { FC } from 'react';

import { EvTitle } from '@evinced-private/ui-common';

import { AccessibilitySummaryContainer } from 'src/apps/dashboard/components/accessibility-summary/AccessibilitySummaryContainer';
import { BusinessUnitsTable } from 'src/apps/dashboard/components/business-units-table';
import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useBusinessUnitsTableData';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';

import { AccessibilityCriticalIssuesChartContainer } from '../../components/accessibility-critical-issues-chart/AccessibilityCriticalIssuesChartContainer';
import { AccessibilityTrendsChartContainer } from '../../components/accessibility-trends-chart/AccessibilityTrendsChartContainer';
import { DashboardFilters } from '../../components/dashboard-filters/DashboardFilters';
import { useConfigurationFilters } from '../../hooks/useConfigurationFilters';
import { useDashboardDataQuery } from '../../hooks/useDashboardDataQuery';

import './Dashboard.scss';

export const AccessibilityDashboard: FC = () => {
	const { filters, setFilters } = useDashboardFiltersStore();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();

	const {
		data: dashboardData,
		isLoading: isDashboardDataLoading,
		error: dashboardDataError
	} = useDashboardDataQuery(filters, labelsExcludedFromDashboard);

	const selectedPeriod = filters?.dateRange?.[0]?.value ? Number(filters.dateRange[0].value) : 30;

	const chartsData = dashboardData?.regularPeriod ?? [];
	const accessibilityData = dashboardData?.doublePeriod ?? [];

	const [businessUnitsQuery] = useBusinessUnitsTableData({
		filters,
		labelsExcludedFromDashboard
	});

	const businessUnitsData = businessUnitsQuery.data;
	const isBusinessUnitsLoading =
		businessUnitsQuery.isLoading ||
		businessUnitsQuery.isPreviousData ||
		(businessUnitsQuery.isStale && businessUnitsQuery.isRefetching);
	const businessUnitsError = businessUnitsQuery.error;

	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	return (
		<div className="accessibility-dashboard">
			<h1 className="accessibility-dashboard-title">Accessibility Dashboard</h1>

			<DashboardFilters
				filters={filters}
				onFilterChange={setFilters}
				filterDefinitions={filterDefinitions}
				filterOptions={filterOptions}
			/>

			<div className="dashboard-content">
				<section
					id="accessibility-summary-section"
					role="group"
					aria-labelledby="accessibility-summary-heading"
				>
					<AccessibilitySummaryContainer
						data={accessibilityData}
						selectedPeriod={selectedPeriod}
						isLoading={isDashboardDataLoading}
						error={dashboardDataError}
					/>
				</section>

				<section id="dashboard-charts-section">
					<EvTitle id="trends-heading" titleText="Accessibility performance trends" />

					<div className="dashboard-charts">
						<AccessibilityTrendsChartContainer />

						<AccessibilityCriticalIssuesChartContainer
							data={chartsData}
							isLoading={isDashboardDataLoading}
							error={dashboardDataError}
						/>
					</div>
				</section>

				<section id="business-units-section">
					<EvTitle
						id="business-units-heading"
						titleText={`${businessUnitDisplayName} Business Units`}
					/>

					<BusinessUnitsTable
						data={businessUnitsData?.rows ?? []}
						isLoading={isBusinessUnitsLoading}
						error={businessUnitsError}
						total={businessUnitsData?.total ?? 0}
						unfilteredTotal={businessUnitsData?.unfilteredTotal ?? 0}
					/>
				</section>
			</div>
		</div>
	);
};
